import React from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, RefreshCw } from "lucide-react";
import useSEO from "../hooks/useSEO";

const CookiesPolicyPage = () => {
  useSEO({
    title: "Politique de cookies",
    description: "Comment Passion Verre utilise les cookies : mesure d'audience anonyme, gestion de vos préférences, conformité RGPD/CNIL.",
  });

  const openConsent = () => window.dispatchEvent(new Event("pv:openConsent"));

  return (
    <div className="min-h-screen bg-[#171717] pt-32 pb-24" data-testid="cookies-policy-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <Cookie className="mx-auto text-[#9A8640] mb-4" size={32} />
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4">Politique de cookies</h1>
          <p className="text-[#DDD4B2]/70">Dernière mise à jour : février 2026</p>
        </div>

        <div className="prose prose-invert max-w-none text-[#DDD4B2]/90 space-y-8 leading-relaxed">
          <section>
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-3">1. Qu'est-ce qu'un cookie ?</h2>
            <p>
              Un cookie est un petit fichier texte stocké dans votre navigateur lorsque vous visitez un site web.
              Il permet au site de mémoriser des informations sur votre visite (préférences, statistiques d'usage)
              pour améliorer votre expérience.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-3">2. Quels cookies utilisons-nous ?</h2>

            <div className="border-l-4 border-[#9A8640] pl-4 my-4">
              <h3 className="font-serif text-xl text-[#DDD4B2] mb-2">🍪 Cookies essentiels (toujours actifs)</h3>
              <p>
                Ces cookies sont indispensables au bon fonctionnement du site : navigation, sécurité, mémorisation
                de vos préférences de consentement aux cookies. Ils ne collectent aucune donnée personnelle et ne
                nécessitent pas votre accord.
              </p>
            </div>

            <div className="border-l-4 border-[#9A8640] pl-4 my-4">
              <h3 className="font-serif text-xl text-[#DDD4B2] mb-2">📊 Cookies de mesure d'audience — PostHog</h3>
              <p className="mb-2">
                <strong>Outil utilisé :</strong> PostHog (chargé automatiquement par la plateforme technique de notre site,
                via les domaines <code className="text-[#9A8640]">ap.emergent.sh</code> et{" "}
                <code className="text-[#9A8640]">assets.emergent.sh</code>).
              </p>
              <p className="mb-2">
                <strong>À quoi ça sert :</strong> comprendre quelles pages sont consultées, d'où viennent les visiteurs
                et améliorer l'ergonomie du site.
              </p>
              <p className="mb-2">
                <strong>Données collectées :</strong> pages visitées, source de trafic, type d'appareil, durée de session.
                Aucune donnée personnelle identifiable n'est stockée.
              </p>
              <p className="mb-2">
                <strong>Durée de conservation :</strong> 13 mois maximum (recommandation CNIL).
              </p>
              <p>
                <strong>Statut :</strong> désactivé par défaut, activé uniquement si vous l'acceptez explicitement dans le bandeau de consentement.
              </p>
            </div>

            <div className="border-l-4 border-[#9A8640] pl-4 my-4">
              <h3 className="font-serif text-xl text-[#DDD4B2] mb-2">📈 Mesure d'audience interne (anonyme)</h3>
              <p>
                Nous utilisons également notre propre système de comptage anonyme (pages vues, sources) pour lequel
                aucun cookie de suivi cross-site n'est déposé. Ce type de mesure est <strong>exempté de consentement</strong>
                selon les recommandations CNIL car strictement limité à l'usage interne.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-3">3. Comment retirer votre consentement ?</h2>
            <p className="mb-4">
              Vous pouvez à tout moment modifier ou retirer votre choix en cliquant sur le lien{" "}
              <strong>« Gérer mes cookies »</strong> présent en pied de page, ou en cliquant sur le bouton ci-dessous :
            </p>
            <button
              onClick={openConsent}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#9A8640] text-[#171717] text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#DDD4B2] transition-colors"
              data-testid="reopen-consent-inline"
            >
              <RefreshCw size={14} /> Modifier mes préférences de cookies
            </button>
            <p className="mt-4 text-sm text-[#DDD4B2]/70">
              Vous pouvez également supprimer les cookies déjà déposés depuis les paramètres de votre navigateur.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-3">4. Durée de conservation de votre choix</h2>
            <p>
              Votre décision (accepter, refuser, personnaliser) est mémorisée pendant <strong>6 mois maximum</strong>,
              conformément aux recommandations de la CNIL. Passé ce délai, le bandeau vous sera à nouveau proposé.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-3">5. Vos droits</h2>
            <p>
              Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés,
              vous disposez d'un droit d'accès, de rectification, d'effacement et d'opposition sur vos données personnelles.
            </p>
            <p className="mt-2">
              Pour exercer ces droits, contactez-nous à{" "}
              <a href="mailto:contact@passionverre.com" className="text-[#9A8640] underline hover:text-[#DDD4B2]">
                contact@passionverre.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#DDD4B2] mb-3">6. En savoir plus</h2>
            <p>
              Pour plus d'informations sur les cookies et vos droits en ligne, consultez le site de la{" "}
              <a href="https://www.cnil.fr/fr/cookies-et-traceurs-que-dit-la-loi" target="_blank" rel="noopener noreferrer" className="text-[#9A8640] underline hover:text-[#DDD4B2]">
                CNIL
              </a>.
            </p>
          </section>

          <div className="pt-8 border-t border-[#9A8640]/25 mt-12">
            <p className="text-sm text-[#DDD4B2]/60">
              <Shield className="inline mr-2" size={14} />
              Ce site respecte les recommandations de la CNIL en matière de cookies et de mesure d'audience.
            </p>
            <Link to="/mentions-legales" className="text-xs text-[#9A8640] hover:text-[#DDD4B2] block mt-2">
              → Voir aussi nos mentions légales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicyPage;
